import { ActionCardModel } from '.'
import assignConditionalItems from './assignConditionalItems'
import calculateScores from './calculateScores'
import enableSeparator from './enableSeparator'
import { FieldTypes, sectionTypes, WidgetType } from './enums'
import makeAssessmentsChecked from './makeAssessmentsChecked'
import { recalculateFormulaNodes } from './recalculateFormulaNodes'
import resetLastActiveSeparator from './resetLastActiveSeparator'
import setFlowStatus from './setFlowStatus'
import { ActionCardData, UpdaterData, UpdaterDto } from './types'

function updateActionCard(actionCard: ActionCardData, updaterDto: Omit<UpdaterDto, 'room'>) {
  const { data, isLocked } = updaterDto
  const ac = JSON.parse(JSON.stringify(actionCard)) as ActionCardData
  const actionCardModel = new ActionCardModel(ac)

  ac.isLocked = isLocked?.toString() === 'false' || isLocked?.toString() === 'true' ? isLocked : ac.isLocked

  ac.sections.forEach((sec, index) => {
    update(sec)

    function update(obj: {
      id: number
      value?: unknown
      checked?: boolean
      isOpened?: boolean
      freeTextValue?: string
      type?: string
      [x: string]: any
    }) {
      for (const key of Object.keys(obj)) {
        const val = obj[key]
        const id = Number(val)
        const payload = data[id]

        if (payload) {
          if (isObjectUsingIsLockedProp(obj, id)) {
            obj.isLocked = payload.isLocked
          }

          if (isObjectUsingStatesProp(obj, payload, id)) {
            obj.states = payload.states
          }

          if (isObjectUsingUpdatedAtProp(obj, payload, id)) {
            obj.updatedAt = payload.updatedAt
          }

          if (isObjectUsingIsAcUpdatedProp(obj, payload, id)) {
            obj.isAcUpdated = payload.isAcUpdated
          }

          if (isObjectUsingIsOpenedProp(obj, payload, id)) {
            obj.isOpened = payload.isOpened

            continue
          }

          if (isObjectUsingIsMinimizedProp(obj, payload, id)) {
            obj.isSectionMinimized = payload.isSectionMinimized

            continue
          }

          if (isObjectUsingValueProp(obj, id)) {
            obj.value = payload.value

            setLastActiveScope(ac, id, obj, data)
          } else if (isObjectUsingCheckedProp(obj, id)) {
            obj.checked = payload.checked

            setLastActiveScope(ac, id, obj, data)

            if (Object.hasOwn(payload, 'freeTextValue')) {
              obj.freeTextValue = payload.freeTextValue
            }
          }

          continue
        } else {
          if (typeof val === 'object' && val !== null) {
            update(val)
          }
        }
      }
    }
  })

  recalculateFormulaNodes(actionCardModel)
  makeAssessmentsChecked(ac)
  assignConditionalItems(actionCardModel)
  enableSeparator(ac)
  calculateScores(ac)
  resetLastActiveSeparator(ac)
  setFlowStatus(ac)

  return ac
}

function isObjectUsingValueProp(obj: Record<string, any>, id: number) {
  return (
    obj.id === id &&
    (obj.type === FieldTypes.Text ||
      obj.type === FieldTypes.Notes ||
      obj.type === FieldTypes.Checkbox ||
      obj.type === FieldTypes.Dropdown ||
      obj.type === FieldTypes.Date ||
      obj.type === FieldTypes.DateTime ||
      obj.type === FieldTypes.Amount ||
      obj.type === FieldTypes.LongText ||
      obj.type === FieldTypes.Number ||
      obj.type === WidgetType.GenericAiPrompt ||
      obj.type === FieldTypes.Email ||
      obj.type === FieldTypes.File ||
      obj.type === FieldTypes.Percentage ||
      obj.type === FieldTypes.Signature ||
      obj.type === WidgetType.AiTeammate ||
      obj.type === WidgetType.ConnectToCrm)
  )
}
function isObjectUsingCheckedProp(obj: Record<string, any>, id: number) {
  return (obj.id === id && Object.hasOwn(obj, 'answer')) || (obj.id === id && Object.hasOwn(obj, 'responses'))
}

function isObjectUsingIsOpenedProp(obj: Record<string, any>, payload: Record<string, any>, id: number) {
  return (
    obj.id === id &&
    (obj.type === sectionTypes.SEPARATOR || obj.type === sectionTypes.CONTENT) &&
    Object.hasOwn(payload, 'isOpened')
  )
}

function isObjectUsingIsMinimizedProp(obj: Record<string, any>, payload: Record<string, any>, id: number) {
  return (
    obj.id === id &&
    (obj.type === sectionTypes.SEPARATOR || obj.type === sectionTypes.CONTENT) &&
    Object.hasOwn(payload, 'isSectionMinimized')
  )
}

function isObjectUsingIsLockedProp(obj: Record<string, any>, id: number) {
  return obj.id === id && obj.type === sectionTypes.SEPARATOR
}

function isObjectUsingStatesProp(obj: Record<string, any>, payload: Record<string, any>, id: number) {
  return obj.id === id && Object.hasOwn(payload, 'states')
}

function isObjectUsingUpdatedAtProp(obj: Record<string, any>, payload: Record<string, any>, id: number) {
  return obj.id === id && Object.hasOwn(payload, 'updatedAt')
}

function isObjectUsingIsAcUpdatedProp(obj: Record<string, any>, payload: Record<string, any>, id: number) {
  return obj.id === id && Object.hasOwn(payload, 'isAcUpdated')
}

function setLastActiveScope(
  ac: ActionCardData,
  id: number,
  obj: { id: number; idOfSep?: number; type?: string },
  data: UpdaterData,
) {
  if (Object.keys(data).some(key => +key === id) && obj.idOfSep) {
    ac.lastActiveSeparator = obj.idOfSep
  }
}

export default updateActionCard
